import React, { useState, useEffect, useRef, useContext, createContext } from 'react'

const PanelContext = createContext({
  flush: false,
  panel: null,
  setHeight: () => {},
})

const Panel = ({ children, name, tabpanel }) => {
  const { flush, panel, setHeight } = useContext(PanelContext)
  const [ status, setStatus ] = useState(panel == name ? 'current' : null)
  const element = useRef()

  const currentPanelEffect = () => {
    const handleResize = () =>
      element.current?.offsetHeight &&
      setHeight(element.current.offsetHeight + 'px')

    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(element.current)

    handleResize()
    const timeout = setTimeout(handleResize, 330)

    return () => {
      resizeObserver.disconnect()
      clearTimeout(timeout)
    }
  }

  const transitioningPanelEffect = () => {
    const timeout = setTimeout(() => setStatus(''), 330)
    return () => clearTimeout(timeout)
  }

  useEffect(() => {
    switch (status) {
      case 'current': return currentPanelEffect()
      case 'transitioning': return transitioningPanelEffect()
    }
  }, [ status ])

  useEffect(() => {
    setStatus(status =>
      panel == name ? 'current' : status == 'current' ? 'transitioning' : null
    )
  }, [ panel ])

  return (
    <div
      id={`${name}-tab-pane`}
      role={tabpanel ? 'tabpanel' : null}
      aria-labelledby={tabpanel && `${name}-tab`}
      aria-hidden={!status}
      className={[
        'widget-panel bg-white',
        flush && 'widget-panel-flush',
        status == 'current' && 'widget-panel-current',
      ].filter(Boolean).join(' ')}
    >
      <div
        ref={element}
        className={`widget-panel-inner ${flush ? 'p-2' : 'p-3 p-sm-4'}`}
      >
        {status && children}
      </div>
    </div>
  )
}

Panel.Collection = ({ children, panel, flush }) => {
  const [ transitioning, setTransitioning ] = useState(false)
  const [ height, setHeight ] = useState('16rem')

  useEffect(() => {
    setTransitioning(true)
    const timeout = setTimeout(() => setTransitioning(false), 330)
    return () => clearTimeout(timeout)
  }, [ height ])

  return (
    <div
      className={[
        'widget-content position-relative overflow-hidden bg-white',
        flush && 'widget-content-flush m-n2',
        transitioning ? 'widget-content-transitioning' : '',
      ].filter(Boolean).join(' ')}
      style={{ height }}
    >
      <PanelContext.Provider value={{ panel, setHeight, flush }}>
        {children}
      </PanelContext.Provider>
    </div>
  )
}

export default Panel
